/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Briefcase, BookMarked, PlayCircle, FlaskConical } from 'lucide-react';
import { getDomainBySlug } from '../config/domains';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import ManhattanKnowledgeShowcase from '../components/showcase/ManhattanKnowledgeShowcase';

// WMS-only "Explore the WMS Showcase" cards. Inline data — no
// component file because this is rendered exactly once.
const WMS_SHOWCASE_LINKS = [
  {
    Icon: Briefcase,
    title: 'Case Studies',
    body: 'Eleven real Manhattan engagements across PkMS and WMi versions. Client identities anonymized; methodologies and outcomes preserved.',
    to: '/domain/wms/case-studies',
  },
  {
    Icon: BookMarked,
    title: 'Resources Library',
    body: 'Twenty-five curated resources across documentation, tools, community, certifications, and reference reading.',
    to: '/domain/wms/resources',
  },
  {
    Icon: PlayCircle,
    title: 'ML Simulation',
    body: 'Trained PPO+curiosity policy in a procedural Unity warehouse. 79,000 training steps with Isaac Sim zone embeddings.',
    to: '/domain/wms/showcase',
  },
  {
    Icon: FlaskConical,
    title: 'Putaway Intelligence',
    body: 'Comparative case study across I-shape vs U-shape warehouse layouts and three putaway strategies. 6.7× efficiency spread quantified across 200 SKUs, 34K orders, 310 locations.',
    to: '/domain/wms/research/putaway-intelligence.html',
    external: true,
  },
];

// Civil-only "Explore Skill.Civil" cards. Inline for now — one card while
// the case-study library grows. Add more entries as further case studies
// (or a Cricket Dashboard preview / resources library) are published.
const CIVIL_SHOWCASE_LINKS = [
  {
    Icon: Briefcase,
    title: 'The Ratwani Legacy',
    body: 'Three generations, 50+ years of government civil contracting across Uttar Pradesh — CPWD, PWD, Railways, Telecom, Education, Healthcare. The family field-knowledge Skill.Civil is being trained against.',
    to: '/domain/civil/case-studies/ratwani',
  },
];

const DOMAIN_CONTENT = {
  wms: {
    eyebrow: 'Warehouse Management',
    headline: 'Manhattan WMi expertise. Real practitioner depth.',
    subheadline:
      '22+ years of Manhattan WMS implementation, migration & AMC for 3PL, retail & eCommerce. Real expertise — not Wikipedia.',
    visibilityLine: 'Your warehouse expertise becomes visible here.',
    status: 'live',
    audienceCards: [
      { entity: 'Individual',   icon: '👤', description: 'Practitioners growing in WMS — implementation, support, consultancy.',                              status: 'live'   },
      { entity: 'Organization', icon: '🏢', description: '3PLs, retailers, distributors deploying WMS knowledge across teams.',                              status: 'soon'   },
      { entity: 'University',   icon: '🎓', description: 'Supply chain programs — faculty expertise, graduate readiness, industry partnerships.',            status: 'future' },
      { entity: 'Institute',    icon: '🏫', description: 'WMS training and certification bodies making programs visible to industry.',                       status: 'future' }
    ],
    features: [
      {
        icon: '📦',
        title: 'SkillBot for WMS',
        body: 'Manhattan, SAP EWM, Blue Yonder. Practitioner-depth answers — not generic AI.'
      },
      {
        icon: '🏭',
        title: '810+ training scenarios',
        body: 'From wave picking to slotting optimization, drawn from real implementations.'
      },
      {
        icon: '🎓',
        title: 'Roadmaps for WMS roles',
        body: 'Implementation lead, support analyst, integration specialist — personalized learning paths.'
      },
      {
        icon: '🤝',
        title: 'Practitioner network',
        body: 'Find mentors and peers via xConnect — purposeful discovery, not random networking.'
      }
    ],
    ctaEyebrow: 'Ready to try it?',
    ctaHeading: 'Get started today.',
    ctaBody: 'Try SkillBot for WMS. No setup required.',
    primaryCta: { type: 'route',  to: '/register', label: 'Try SkillBot Now' },
    secondaryCta: { type: 'route', to: '/tour',     label: 'Take the platform tour' }
  },

  iseries: {
    eyebrow: 'IBM iSeries',
    headline: 'Modernization without losing knowledge.',
    subheadline:
      'AS400 RPG, CL, DB2 development, modernization and production support. 30,000+ iSeries installations in India running code nobody fully understands anymore.',
    visibilityLine: 'Your iSeries mastery deserves to be seen.',
    status: 'soon',
    audienceCards: [
      { entity: 'Individual',   icon: '👤', description: 'RPGLE / CLLE engineers maintaining and modernizing iSeries systems.',              status: 'live'   },
      { entity: 'Organization', icon: '🏢', description: 'IT shops with iSeries footprint — knowledge preservation, modernization risk reduction.', status: 'soon'   },
      { entity: 'University',   icon: '🎓', description: 'Computer science programs covering legacy systems and enterprise modernization.',  status: 'future' },
      { entity: 'Institute',    icon: '🏫', description: 'iSeries-focused training and certification programs.',                             status: 'future' }
    ],
    features: [
      {
        icon: '💻',
        title: 'SkillBot for iSeries',
        body: 'RPGLE, CLLE, CLP, DDS, SQL. Built for the people quietly keeping critical systems running.'
      },
      {
        icon: '🔬',
        title: 'SkillCode Intelligence',
        body: 'Code intelligence for iSeries: modification risk scoring, dependency analysis, STRSRVJOB runtime tracing, Job Trace UI. Plain English answers about your codebase.'
      },
      {
        icon: '🧠',
        title: 'Knowledge preservation',
        body: 'Capture what senior developers know before they retire — into a living, queryable system.'
      },
      {
        icon: '🤝',
        title: 'iSeries practitioner network',
        body: 'Find mentors and opportunities in a scarce-skill community via xConnect.'
      }
    ],
    ctaEyebrow: 'Want early access?',
    ctaHeading: 'Talk to us.',
    ctaBody: 'We are inviting select iSeries professionals for early access.',
    primaryCta: {
      type: 'mailto',
      href: 'mailto:info@skilldelivery.co.in?subject=Early%20access%20-%20iSeries',
      label: 'Book a demo with Lalit'
    },
    secondaryCta: { type: 'route', to: '/skillcode', label: 'Explore SkillCode' }
  },

  civil: {
    eyebrow: 'Civil Construction',
    headline: 'Project intelligence for builders who know the ground.',
    subheadline:
      '40+ years legacy in government contracting, BOQ, EVM & project tracking for PWD/CPWD. ERPNext for project tracking + Cricket Dashboard for analytics.',
    visibilityLine: 'Your engineering judgment counts here.',
    status: 'soon',
    audienceCards: [
      { entity: 'Individual',   icon: '👤', description: 'Site engineers, project managers, government contractors.',                       status: 'soon'   },
      { entity: 'Organization', icon: '🏢', description: 'Construction firms, infrastructure developers, PWD/CPWD teams.',                  status: 'soon'   },
      { entity: 'University',   icon: '🎓', description: 'Civil engineering programs — bridge to government contracting practice.',         status: 'future' },
      { entity: 'Institute',    icon: '🏫', description: 'Vocational construction training and trade certification bodies.',                status: 'future' }
    ],
    features: [
      {
        icon: '🏗️',
        title: 'SkillBot for Civil',
        body: 'Trades, materials, execution, RCC, CPWD billing. Real expertise from decades of practice.'
      },
      {
        icon: '🏏',
        title: 'Cricket Dashboard',
        body: 'EVM for Civil Construction — SPI/CPI/TCPI gauges, S-Curve charts, work package progress mapped to PWD/CPWD RA-Bill cycles. Available to Civil organizations.'
      },
      {
        icon: '🎓',
        title: 'Trade-specific learning',
        body: 'Codes change. Practices evolve. SkillBot learns with you — concrete, steel, finishing.'
      },
      {
        icon: '🤝',
        title: 'Builder network',
        body: 'Government contractors, infrastructure developers, PWD/CPWD teams — connect via xConnect.'
      }
    ],
    ctaEyebrow: 'Want early access?',
    ctaHeading: 'Talk to us.',
    ctaBody: 'We are inviting select civil construction firms for early access — ask about Cricket Dashboard demo.',
    primaryCta: {
      type: 'mailto',
      href: 'mailto:info@skilldelivery.co.in?subject=Early%20access%20-%20Civil%20Construction',
      label: 'Book a demo with Lalit'
    },
    secondaryCta: { type: 'route', to: '/cricket-dashboard', label: 'See Cricket Dashboard' }
  },

  apparel: {
    eyebrow: 'Apparel & Stitching',
    headline: '65 years of MSME garment mastery.',
    subheadline:
      "Garment production, fabric, stitching operations, quality control, textile manufacturing. India is the world's 2nd largest exporter — built with master tailors who know the craft.",
    visibilityLine: 'Your craft has worth — we make it visible.',
    status: 'soon',
    audienceCards: [
      { entity: 'Individual',   icon: '👤', description: 'Tailors, designers, production supervisors, quality professionals.',              status: 'soon'   },
      { entity: 'Organization', icon: '🏢', description: 'Garment manufacturers, MSME exporters, fabric and stitching units.',              status: 'soon'   },
      { entity: 'University',   icon: '🎓', description: 'Fashion and textile institutions — practical depth alongside curriculum.',        status: 'future' },
      { entity: 'Institute',    icon: '🏫', description: 'Vocational garment training and skill development programs.',                     status: 'future' }
    ],
    features: [
      {
        icon: '✂️',
        title: 'SkillBot for Apparel',
        body: 'Fabric relaxation, quality failures, production line efficiency — wisdom from decades of work.'
      },
      {
        icon: '🧵',
        title: 'Vocational training depth',
        body: 'MSME garment production, quality control, textile manufacturing — practitioner-level learning paths.'
      },
      {
        icon: '🎓',
        title: 'Continuous learning',
        body: 'Trends shift. Techniques evolve. SkillBot learns with you — patterns, drape, finish.'
      },
      {
        icon: '🤝',
        title: 'Maker network',
        body: 'Connect designers, tailors, manufacturers, and brands via xConnect.'
      }
    ],
    ctaEyebrow: 'Want early access?',
    ctaHeading: 'Talk to us.',
    ctaBody: 'We are inviting select apparel professionals for early access.',
    primaryCta: {
      type: 'mailto',
      href: 'mailto:info@skilldelivery.co.in?subject=Early%20access%20-%20Apparel',
      label: 'Book a demo with Lalit'
    },
    secondaryCta: { type: 'route', to: '/tour', label: 'Take the platform tour' }
  },

  erpnext: {
    eyebrow: 'ERPNext',
    headline: 'One ERP. Zero licensing cost. Built for India.',
    subheadline:
      'ERPNext base implementation — distribution, manufacturing, civil contracting. Open source. Full GST built-in. PoC completed and ready for first client implementations.',
    visibilityLine: "Your business systems knowledge isn't invisible anymore.",
    status: 'poc',
    audienceCards: [
      { entity: 'Individual',   icon: '👤', description: 'Independent ERPNext consultants and implementers.',                               status: 'soon'   },
      { entity: 'Organization', icon: '🏢', description: 'MSMEs evaluating ERP — distribution, manufacturing, services.',                   status: 'soon'   },
      { entity: 'University',   icon: '🎓', description: 'Business and management programs teaching modern ERP.',                           status: 'future' },
      { entity: 'Institute',    icon: '🏫', description: 'Vocational ERP training and certification programs.',                             status: 'future' }
    ],
    features: [
      {
        icon: '📦',
        title: 'Inventory & Procurement',
        body: 'Multi-warehouse, batches, serial numbers, PO → GRN → Invoice with approval flows.'
      },
      {
        icon: '💰',
        title: 'Accounts & GST',
        body: 'Full Indian GST, TDS, e-invoicing, P&L, balance sheet — auto-generated.'
      },
      {
        icon: '🏪',
        title: 'POS, HR & Manufacturing',
        body: 'Offline-capable POS, attendance & payroll with Indian compliance, manufacturing & projects.'
      },
      {
        icon: '🤝',
        title: 'SkillBot + SkillDelivery embedded',
        body: 'AI-powered staff training and domain knowledge layered on top of your operational ERP.'
      }
    ],
    ctaEyebrow: 'Want to discuss ERP needs?',
    ctaHeading: 'Talk to Lalit.',
    ctaBody:
      'Save ₹5-15L/year in licensing. PoC completed locally — ready for first client implementations, base version, scoped per project.',
    primaryCta: {
      type: 'mailto',
      href: 'mailto:info@skilldelivery.co.in?subject=ERPNext%20implementation%20-%20discuss%20requirements',
      label: 'Discuss your ERP needs'
    },
    secondaryCta: { type: 'route', to: '/tour', label: 'Take the platform tour' }
  }
};

// ── Decorative radial glows (shared across both branches) ───────────
const BrandGlows = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-32 -right-32"
      style={{
        width: '500px',
        height: '500px',
        background:
          'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-24 -left-24"
      style={{
        width: '400px',
        height: '400px',
        background:
          'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
      }}
    />
  </>
);

// ── Audience-fit pill: live / soon / future — cyan-family only ──────
const AudienceStatusBadge = ({ status }) => {
  if (status === 'live') {
    return (
      <span className="inline-block rounded-brandPill bg-brand-cyan/10 text-brand-cyanDark border border-brand-cyan/30 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider">
        Live now
      </span>
    );
  }
  if (status === 'soon') {
    return (
      <span className="inline-block rounded-brandPill bg-brand-bgOff2 text-brand-cyan px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider">
        Coming soon
      </span>
    );
  }
  // future
  return (
    <span className="inline-block rounded-brandPill bg-brand-bgOff text-brand-muted2 border border-brand-border px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider">
      Future
    </span>
  );
};

// ── Status badge: live (green) / poc (orange) / soon (cyan) ─────────
const StatusBadge = ({ status }) => {
  if (status === 'live') {
    return (
      <span className="rounded-brandPill bg-green-50 text-brand-green px-4 py-1 text-xs font-bold uppercase tracking-wider">
        Live Now
      </span>
    );
  }
  if (status === 'poc') {
    return (
      <span className="rounded-brandPill bg-orange-50 text-orange-700 border border-orange-200 px-4 py-1 text-xs font-bold uppercase tracking-wider">
        POC Ready
      </span>
    );
  }
  return (
    <span className="rounded-brandPill bg-brand-bgOff2 text-brand-cyan px-4 py-1 text-xs font-bold uppercase tracking-wider">
      Coming Soon
    </span>
  );
};

// ── CTA renderers (route vs mailto) ─────────────────────────────────
const PrimaryCta = ({ cta }) => {
  const cls =
    'inline-flex items-center justify-center px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2';
  if (cta.type === 'mailto') {
    return <a href={cta.href} className={cls}>{cta.label}</a>;
  }
  return <Link to={cta.to} className={cls}>{cta.label}</Link>;
};

const SecondaryCta = ({ cta }) => {
  const cls =
    'inline-flex items-center justify-center px-6 py-3 rounded-brand border-2 border-brand-cyan text-brand-cyan font-semibold text-sm bg-white hover:bg-brand-bgOff2 hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2';
  if (cta.type === 'mailto') {
    return <a href={cta.href} className={cls}>{cta.label}</a>;
  }
  return <Link to={cta.to} className={cls}>{cta.label}</Link>;
};

const DomainLanding = () => {
  const { name } = useParams();
  const domain = getDomainBySlug(name);
  const content = domain ? DOMAIN_CONTENT[domain.slug] : null;

  // ─── Not-found fallback ───────────────────────────────────────────
  if (!domain || !content) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
        <PublicNav />
        <BrandGlows />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 sm:pt-20 pb-16">
          <div className="mt-10">
            <Link
              to="/welcome"
              className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
            >
              ← Back to home
            </Link>
          </div>
          <div className="mt-10 text-center">
            <div className="text-5xl mb-4" aria-hidden="true">❓</div>
            <h1 className="text-4xl sm:text-5xl font-bold text-brand-text tracking-tight">
              Domain not found
            </h1>
            <p className="mt-5 text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
              We couldn't find that domain. Try one of our five: WMS, iSeries, Civil, Apparel, or ERPNext.
            </p>
          </div>
        </div>

        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">
      <PublicNav />
      <BrandGlows />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Back link ── */}
        <div className="mt-10">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to home
          </Link>
        </div>

        {/* ── Hero ── */}
        <header className="text-center mt-10 sm:mt-12">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            {content.eyebrow}
          </p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            {content.headline}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            {content.subheadline}
          </p>
          {content.visibilityLine && (
            <p className="mt-5 text-lg sm:text-xl italic text-brand-cyan font-medium leading-relaxed max-w-2xl mx-auto">
              {content.visibilityLine}
            </p>
          )}
          <div className="mt-6 flex justify-center">
            <StatusBadge status={content.status} />
          </div>
        </header>

        {/* ── Who this is for ── */}
        {content.audienceCards && (
          <section aria-label={`Who ${domain.shortLabel} expertise is for`} className="mt-16">
            <p className="text-center text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
              Who this is for
            </p>
            <h2 className="mt-3 text-center text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
              Who {domain.shortLabel} expertise is for:
            </h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {content.audienceCards.map((card) => (
                <div
                  key={card.entity}
                  className="flex flex-col bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand"
                >
                  <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                    <span className="text-2xl leading-none" aria-hidden="true">{card.icon}</span>
                  </div>
                  <h3 className="font-bold text-base text-brand-text">
                    {card.entity}
                  </h3>
                  <div className="mt-2">
                    <AudienceStatusBadge status={card.status} />
                  </div>
                  <p className="mt-3 text-sm text-brand-muted leading-relaxed">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Feature grid ── */}
        <section
          aria-label="What you get"
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
        >
          {content.features.map((f) => (
            <div
              key={f.title}
              className="bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                <span className="text-2xl leading-none" aria-hidden="true">{f.icon}</span>
              </div>
              <h3 className="font-bold text-base text-brand-text">
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </section>

        {/* ── Manhattan WMS Capabilities (WMS only) ── */}
        {domain.slug === 'wms' && <ManhattanKnowledgeShowcase />}

        {/* ── Explore the WMS Showcase (WMS only) ── */}
        {domain.slug === 'wms' && (
          <section id="wms-showcase" aria-label="Explore the WMS showcase" className="mt-20">
            <p className="text-center text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
              Explore
            </p>
            <h2 className="mt-3 text-center text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
              WMS Showcase
            </h2>
            <p className="mt-4 text-center text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
              Four views into twenty-two years of Manhattan WMS practice.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {WMS_SHOWCASE_LINKS.map(({ Icon, title, body, to, external }) => {
                const cardClass = "group flex flex-col bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 no-underline";
                const cardInner = (
                  <>
                    <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-base text-brand-text">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                      {body}
                    </p>
                    <p className="mt-auto pt-4 text-sm font-semibold text-brand-cyan group-hover:translate-x-1 transition-transform">
                      Explore →
                    </p>
                  </>
                );
                return external ? (
                  <a key={to} href={to} className={cardClass}>
                    {cardInner}
                  </a>
                ) : (
                  <Link key={to} to={to} className={cardClass}>
                    {cardInner}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Explore Skill.Civil (Civil only) ── */}
        {domain.slug === 'civil' && (
          <section id="civil-showcase" aria-label="Explore Skill.Civil" className="mt-20">
            <p className="text-center text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
              Explore
            </p>
            <h2 className="mt-3 text-center text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
              Skill<span className="text-brand-blue">.</span>Civil
            </h2>
            <p className="mt-4 text-center text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
              The families and firms behind the field knowledge Skill.Civil
              is being trained against.
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 max-w-3xl mx-auto">
              {CIVIL_SHOWCASE_LINKS.map(({ Icon, title, body, to, external }) => {
                const cardClass = "group flex flex-col bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2 no-underline";
                const cardInner = (
                  <>
                    <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-brand-cyan" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-base text-brand-text">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                      {body}
                    </p>
                    <p className="mt-auto pt-4 text-sm font-semibold text-brand-cyan group-hover:translate-x-1 transition-transform">
                      Read →
                    </p>
                  </>
                );
                return external ? (
                  <a key={to} href={to} className={cardClass}>
                    {cardInner}
                  </a>
                ) : (
                  <Link key={to} to={to} className={cardClass}>
                    {cardInner}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Final CTA ── */}
        <section className="mt-20 text-center">
          <p className="text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
            {content.ctaEyebrow}
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-brand-text tracking-tight">
            {content.ctaHeading}
          </h2>
          <p className="mt-4 text-base text-brand-muted leading-relaxed max-w-2xl mx-auto">
            {content.ctaBody}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <PrimaryCta cta={content.primaryCta} />
            <SecondaryCta cta={content.secondaryCta} />
          </div>
        </section>

        {/* ── Inline footer ── */}
        <footer className="text-center mt-16">
          <Link
            to="/welcome"
            className="text-sm text-brand-cyan hover:text-brand-cyanDark hover:underline font-medium"
          >
            ← Back to home
          </Link>
        </footer>

      </div>

      <PublicFooter />
    </div>
  );
};

export default DomainLanding;
